import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LogOut, Search, ArrowLeft } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MediaCard } from "@/components/media/MediaCard";
import { MediaEditDialog } from "@/components/media/MediaEditDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MediaUploader } from "@/components/media/MediaUploader";
import { FolderTree } from "@/components/media/FolderTree";
import { BulkActionsBar } from "@/components/media/BulkActionsBar";
import { MediaPreviewModal } from "@/components/media/MediaPreviewModal";
import { MediaListView } from "@/components/media/MediaListView";
import { ViewToggle } from "@/components/media/ViewToggle";
import { SortDropdown, SortOption } from "@/components/media/SortDropdown";
import { MoveFolderDialog } from "@/components/media/MoveFolderDialog";
import { BulkTagDialog } from "@/components/media/BulkTagDialog";
import { UsageWarningDialog } from "@/components/media/UsageWarningDialog";

export default function MediaLibraryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [editingMedia, setEditingMedia] = useState<any>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [previewMedia, setPreviewMedia] = useState<any>(null);
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<SortOption>('date-desc');
  const [moveFolderOpen, setMoveFolderOpen] = useState(false);
  const [bulkTagOpen, setBulkTagOpen] = useState(false);
  const [page, setPage] = useState(0);
  const itemsPerPage = 24;

  const { data: mediaFiles, isLoading } = useQuery({
    queryKey: ['media-library', selectedFolderId],
    queryFn: async () => {
      let query = supabase
        .from('media_library')
        .select(`
          id, filename, storage_path, file_size, mime_type,
          alt_text, title, width, height,
          thumbnail_path, medium_path, large_path,
          original_size, compressed_size, compression_ratio,
          is_public, folder_id, created_at, updated_at,
          media_tag_relations (
            media_tags (
              name
            )
          )
        `);

      if (selectedFolderId) {
        query = query.eq('folder_id', selectedFolderId);
      } else if (selectedFolderId === null) {
        // Show only items without folder when "All Media" is selected
        query = query.is('folder_id', null);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const media = mediaFiles?.find(m => m.id === id);
      if (!media) throw new Error('Media not found');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('media-library')
        .remove([media.storage_path]);

      if (storageError) throw storageError;

      // Delete from database (cascade will handle relations)
      const { error: dbError } = await supabase
        .from('media_library')
        .delete()
        .eq('id', id);

      if (dbError) throw dbError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['media-library'] });
      toast.success('Media deleted successfully');
      setDeletingId(null);
      setSelectedIds([]);
    },
    onError: (error: any) => {
      toast.error('Failed to delete media');
      console.error('Delete error:', error);
    },
  });

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = () => {
    if (selectedIds.length === 0) return;
    // Check if any have usage first
    setDeletingId(selectedIds[0]); // Use first for demo, in production check all
  };

  const handleSelectAll = () => {
    if (!filteredAndSortedMedia) return;
    setSelectedIds(filteredAndSortedMedia.map(m => m.id));
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Filter media
  let filteredMedia = mediaFiles?.filter(media =>
    media.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
    media.alt_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    media.title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Extract tags for filtering
  const processedMedia = filteredMedia?.map(media => ({
    ...media,
    tags: media.media_tag_relations?.map((rel: any) => rel.media_tags.name) || []
  }));

  // Sort media
  const filteredAndSortedMedia = processedMedia?.sort((a, b) => {
    switch (sortBy) {
      case 'date-desc':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'date-asc':
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case 'name-asc':
        return a.filename.localeCompare(b.filename);
      case 'name-desc':
        return b.filename.localeCompare(a.filename);
      case 'size-desc':
        return b.file_size - a.file_size;
      case 'size-asc':
        return a.file_size - b.file_size;
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil((filteredAndSortedMedia?.length || 0) / itemsPerPage);
  const paginatedMedia = filteredAndSortedMedia?.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage
  );

  const handlePreviewNavigation = (direction: 'prev' | 'next') => {
    if (!previewMedia || !filteredAndSortedMedia) return;
    const currentIndex = filteredAndSortedMedia.findIndex(m => m.id === previewMedia.id);
    const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex >= 0 && newIndex < filteredAndSortedMedia.length) {
      setPreviewMedia(filteredAndSortedMedia[newIndex]);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold">Media Library</h1>
            <p className="text-muted-foreground mt-2">
              Manage your images and media files
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/blog')} variant="outline">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Blog
            </Button>
            <Button onClick={handleLogout} variant="outline">
              <LogOut className="mr-2 h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="col-span-3">
            <FolderTree
              selectedFolderId={selectedFolderId}
              onSelectFolder={setSelectedFolderId}
            />
          </div>

          {/* Main Content */}
          <div className="col-span-9">
            <Tabs defaultValue="library" className="space-y-4">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="library">Media Library</TabsTrigger>
                <TabsTrigger value="upload">Upload New</TabsTrigger>
              </TabsList>

              <TabsContent value="library" className="space-y-4">
                {/* Toolbar */}
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                      placeholder="Search media..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setPage(0);
                      }}
                      className="pl-10"
                    />
                  </div>

                  <ViewToggle view={view} onViewChange={setView} />
                  <SortDropdown value={sortBy} onChange={setSortBy} />
                  
                  {selectedIds.length === 0 && filteredAndSortedMedia && filteredAndSortedMedia.length > 0 && (
                    <Button variant="outline" size="sm" onClick={handleSelectAll}>
                      Select All
                    </Button>
                  )}
                </div>

                {/* Content */}
                {isLoading ? (
                  <div className="text-center py-12 text-muted-foreground">
                    Loading...
                  </div>
                ) : paginatedMedia && paginatedMedia.length > 0 ? (
                  <>
                    {view === 'grid' ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                        {paginatedMedia.map((media) => (
                          <MediaCard
                            key={media.id}
                            media={media}
                            onEdit={setEditingMedia}
                            onDelete={() => setDeletingId(media.id)}
                            selectable={true}
                            selected={selectedIds.includes(media.id)}
                            onSelect={() => handleToggleSelect(media.id)}
                            onPreview={() => setPreviewMedia(media)}
                            tags={media.tags}
                          />
                        ))}
                      </div>
                    ) : (
                      <MediaListView
                        media={paginatedMedia}
                        selectedIds={selectedIds}
                        onToggleSelect={handleToggleSelect}
                        onEdit={setEditingMedia}
                        onDelete={setDeletingId}
                        onPreview={setPreviewMedia}
                      />
                    )}

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex justify-center gap-2 mt-6">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page === 0}
                          onClick={() => setPage(p => p - 1)}
                        >
                          Previous
                        </Button>
                        <span className="flex items-center px-4 text-sm text-muted-foreground">
                          Page {page + 1} of {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={page === totalPages - 1}
                          onClick={() => setPage(p => p + 1)}
                        >
                          Next
                        </Button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    {searchQuery ? 'No media found matching your search' : 'No media files yet. Upload some to get started!'}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="upload">
                <MediaUploader 
                  onUploadComplete={() => {
                    queryClient.invalidateQueries({ queryKey: ['media-library'] });
                  }}
                  folderId={selectedFolderId}
                />
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      <Footer />

      {/* Dialogs */}
      <MediaEditDialog
        media={editingMedia}
        open={!!editingMedia}
        onOpenChange={(open) => !open && setEditingMedia(null)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['media-library'] });
          setEditingMedia(null);
        }}
      />

      <UsageWarningDialog
        open={!!deletingId}
        onOpenChange={(open) => !open && setDeletingId(null)}
        mediaId={deletingId || ''}
        onConfirm={() => deletingId && deleteMutation.mutate(deletingId)}
      />

      <MediaPreviewModal
        media={previewMedia}
        open={!!previewMedia}
        onOpenChange={(open) => !open && setPreviewMedia(null)}
        onPrevious={() => handlePreviewNavigation('prev')}
        onNext={() => handlePreviewNavigation('next')}
        hasPrevious={previewMedia && filteredAndSortedMedia ? 
          filteredAndSortedMedia.findIndex(m => m.id === previewMedia.id) > 0 : false}
        hasNext={previewMedia && filteredAndSortedMedia ? 
          filteredAndSortedMedia.findIndex(m => m.id === previewMedia.id) < filteredAndSortedMedia.length - 1 : false}
      />

      <MoveFolderDialog
        open={moveFolderOpen}
        onOpenChange={setMoveFolderOpen}
        mediaIds={selectedIds}
      />

      <BulkTagDialog
        open={bulkTagOpen}
        onOpenChange={setBulkTagOpen}
        mediaIds={selectedIds}
      />

      <BulkActionsBar
        selectedCount={selectedIds.length}
        onDelete={handleBulkDelete}
        onMoveToFolder={() => setMoveFolderOpen(true)}
        onAddTags={() => setBulkTagOpen(true)}
        onClearSelection={handleClearSelection}
      />
    </div>
  );
}
